import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { checkEmailProfessional } from '@/utils/emailCompanyCoherence';
import { logger } from '@/utils/productionLogger';
import { analyzeCampaignDateReliability } from '@/utils/dateReliabilityScoring';
import { analyzeAllReferentsEmailCoherence } from '@/utils/emailCompanyCoherence';
import { validateDeviceAndIPDiversity, detectFraudPatterns } from '@/utils/deviceValidation';
import { getScoredReferents, getDataAvailabilityFlags } from '@/utils/referentFilters';
import { computeRelationCheck } from '@/utils/relationValidation';

interface Referent {
  id: string;
  name: string;
  email: string;
  position?: string;
  status: string;
  company?: string;
  ipAddress?: string;
  deviceFingerprint?: string;
  isEmailProfessional: boolean;
  hasSigned: boolean;
  // ✅ AJOUT : Colonnes dates candidat
  candidate_collaboration_start_date?: string;
  candidate_collaboration_end_date?: string;
  candidate_still_working_together?: boolean;
  // ✅ AJOUT : Dates référent exposées directement
  referent_collaboration_start_date?: string;
  referent_collaboration_end_date?: string;
  referent_still_works_at_company?: boolean;
  referent_still_working_together?: boolean; // ✅ NOUVEAU : Champ pour la logique des dates
  // ✅ PHASE 1 : Colonnes relation
  relation_type?: string; // Type attendu par le candidat/recruteur
  relation_type_confirmed?: string; // Type confirmé par le référent
}

interface CandidateFingerprint {
  deviceFingerprint?: string;
  ipAddress?: string;
  email?: string; // ✅ NOUVEAU : Email du candidat pour détecter les similitudes
}

export const useReportReliability = () => {
  // Calculate reliability score based on real data including date coherence (CORRIGÉ)
  const calculateReliabilityScore = useCallback((
    referents: Referent[], 
    dateReliabilityScore?: number, 
    emailCoherenceScore?: number,
    candidateFingerprint?: CandidateFingerprint
  ) => {
    // Filtrer pour utiliser uniquement les référents complétés dans les calculs
    const scoredReferents = getScoredReferents(referents);
    
    // Sécurité : si aucun référent complété
    if (scoredReferents.length === 0) {
      return {
        score: 0,
        checks: { 
          emailVerified: false, 
          ipDiversity: false, 
          charterSigned: false, 
          deviceDiversity: false,
          dateCoherence: false 
        },
        hasEnoughDataForScores: false
      };
    }
    
    // Utiliser scoredReferents pour tous les calculs ci-dessous
    const referentsForCalculation = scoredReferents;

    // Check professional emails + company coherence (sur référents complétés uniquement)
    const professionalEmails = referentsForCalculation.filter(r => r.isEmailProfessional).length;
    const professionalEmailScore = (professionalEmails / referentsForCalculation.length) * 100;
    
    // Combine professional email validation with company coherence
    const emailCoherenceWeight = emailCoherenceScore || 100; // Default to 100% if not provided
    const emailScore = (professionalEmailScore * 0.6) + (emailCoherenceWeight * 0.4); // 60% professional, 40% coherence

    // CORRECTION MAJEURE : Nouvelle logique device/IP avec vérification candidat (sur référents complétés uniquement)
    const deviceIPValidation = validateDeviceAndIPDiversity(
      referentsForCalculation.map(r => ({
        id: r.id,
        name: r.name,
        deviceFingerprint: r.deviceFingerprint,
        ipAddress: r.ipAddress
      })),
      candidateFingerprint
    );

    const deviceScore = deviceIPValidation.deviceValidation.score;
    const ipScore = deviceIPValidation.ipValidation.score;

    // Détecter fraude potentielle (sur référents complétés uniquement)
    const fraudDetection = detectFraudPatterns(
      referentsForCalculation.map(r => ({
        id: r.id,
        name: r.name,
        email: r.email, // ✅ NOUVEAU : Passer l'email pour la détection de fraude
        deviceFingerprint: r.deviceFingerprint,
        ipAddress: r.ipAddress
      })),
      candidateFingerprint
    );

    // Si fraude détectée, pénaliser sévèrement
    let fraudPenalty = 0;
    if (fraudDetection.isSuspicious) {
      if (fraudDetection.suspicionLevel === 'high') {
        fraudPenalty = 30; // -30 points sur le score total
      } else if (fraudDetection.suspicionLevel === 'medium') {
        fraudPenalty = 15; // -15 points sur le score total
      }
    }

    // Check charter signatures (sur référents complétés uniquement)
    const signedCharters = referentsForCalculation.filter(r => r.hasSigned).length;
    const charterScore = (signedCharters / referentsForCalculation.length) * 100;

    // Date coherence score (convert 0-10 scale to 0-100)
    const dateScore = dateReliabilityScore ? (dateReliabilityScore / 10) * 100 : 100;

    // ✅ PHASE 1 : Check relation type (sur référents complétés uniquement)
    const relationChecks = referentsForCalculation.map(r => 
      computeRelationCheck(r.relation_type, r.relation_type_confirmed)
    );
    const relationScore = (relationChecks.filter(c => c.isValid).length / referentsForCalculation.length) * 100;

    // Weighted average (16.67% each criterion = 6 critères) - fraud penalty
    const baseScore = Math.round((emailScore + ipScore + charterScore + deviceScore + dateScore + relationScore) / 6);
    const totalScore = Math.max(0, baseScore - fraudPenalty);

    // Calculer les flags de disponibilité des données
    const dataFlags = getDataAvailabilityFlags(referents);
    
    return {
      score: totalScore,
      checks: {
        emailVerified: emailScore >= 70,
        ipDiversity: ipScore >= 70,
        charterSigned: charterScore >= 70,
        deviceDiversity: deviceScore >= 70,
        dateCoherence: dateScore >= 70,
        relationConformity: relationScore >= 70 // ✅ PHASE 1 : Nouveau check
      },
      fraudDetection: fraudDetection.isSuspicious ? {
        level: fraudDetection.suspicionLevel,
        reasons: fraudDetection.reasons,
        penalty: fraudPenalty
      } : undefined,
      hasEnoughDataForScores: dataFlags.hasEnoughDataForScores
    };
  }, []);

  // Get candidate fingerprint data from campaign submission
  const getCandidateFingerprint = useCallback(async (campaignId: string): Promise<CandidateFingerprint> => {
    if (!campaignId) {
      console.warn('getCandidateFingerprint called without campaignId');
      return {};
    }

    try {
      logger.debug('Fetching candidate fingerprint for campaign', { campaignId });
      
      // 1. Récupérer les données de soumission (device/IP)
      const { data: submission, error } = await supabase
        .from('candidate_referent_submissions')
        .select('ip_address, device_fingerprint_hash')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.warn('Error fetching candidate submission:', error);
      }

      // 2. ✅ NOUVEAU : Récupérer l'email du candidat depuis la campagne
      const { data: campaign, error: campaignError } = await supabase
        .from('campaigns')
        .select('candidate_email')
        .eq('id', campaignId)
        .single();

      if (campaignError) {
        console.warn('Error fetching candidate email:', campaignError);
      }

      const result: CandidateFingerprint = {
        deviceFingerprint: (submission as any)?.device_fingerprint_hash || undefined,
        ipAddress: (submission as any)?.ip_address?.toString() || undefined,
        email: (campaign as any)?.candidate_email || undefined // ✅ Email candidat
      };

      logger.debug('Candidate fingerprint retrieved', { 
        hasDevice: !!result.deviceFingerprint,
        hasIP: !!result.ipAddress,
        hasEmail: !!result.email,
        campaignId 
      });

      return result;
    } catch (err) {
      console.error('Error in getCandidateFingerprint:', err);
      return {};
    }
  }, []);

  // Get real referents data from campaign
  const getReferentsFromCampaign = useCallback(async (campaignId: string): Promise<Referent[]> => {
    if (!campaignId) {
      console.warn('getReferentsFromCampaign called without campaignId');
      return [];
    }

    try {
      logger.debug('Fetching referents for campaign', { campaignId });
      // First get campaign data to access ai_extracted_data
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('ai_extracted_data')
        .eq('id', campaignId)
        .single();

      if (campaignError) {
        console.error('Error fetching campaign data:', campaignError);
        // Continue même si la campagne échoue, on aura juste pas les vraies entreprises
      }

      const { data: referents, error } = await supabase
        .from('referents')
        .select(`
          id,
          name,
          email,
          position,
          status,
          experience_id,
          relation_type,
          candidate_collaboration_start_date,
          candidate_collaboration_end_date,
          candidate_still_working_together,
          referent_responses (*)
        `)
        .eq('campaign_id', campaignId)
        .limit(100); // Limitation pour éviter les requêtes trop lourdes

      if (error) {
        console.error('Error fetching referents:', error);
        return [];
      }

      

      const processedReferents = (referents || []).map((referent: any) => {
        // Get the real company from multiple sources (with fallback strategies)
        let company = 'Entreprise non spécifiée';

        const aiData = (campaignData as any)?.ai_extracted_data as any;
        
        // Stratégie 1: Via experience_id (méthode principale)
        if (aiData?.experiences && referent.experience_id) {
          const experience = aiData.experiences.find(
            (exp: any) => exp.id === referent.experience_id
          );
          if (experience?.company) {
            company = experience.company;
          }
        }

        // Stratégie 2: Fallback - Chercher dans recommendedReferents par email/nom
        if (company === 'Entreprise non spécifiée' && aiData?.recommendedReferents) {
          const matchingRecommended = aiData.recommendedReferents.find((rec: any) => {
            return rec.email === referent.email || 
                   (rec.suggestedName && rec.suggestedName.toLowerCase().includes(referent.name.toLowerCase()));
          });
          
          if (matchingRecommended?.experienceId && aiData.experiences) {
            const experience = aiData.experiences.find((exp: any) => exp.id === matchingRecommended.experienceId);
            if (experience?.company) {
              company = experience.company;
            }
          }
        }

        // Stratégie 3: Fallback - Utiliser la première expérience disponible si pas d'autre correspondance
        if (company === 'Entreprise non spécifiée' && aiData?.experiences && aiData.experiences.length > 0) {
          company = aiData.experiences[0].company || 'Entreprise non spécifiée';
        }

        // Get IP and device fingerprint from responses (using type assertion for compatibility)
        const response = referent.referent_responses?.[0] as any;
        const ipAddress = response?.ip_address;
        const deviceFingerprint = response?.device_fingerprint_hash;

        return {
          id: referent.id,
          name: referent.name,
          email: referent.email,
          position: referent.position || undefined,
          status: referent.status,
          company,
          ipAddress: ipAddress?.toString(),
          deviceFingerprint: deviceFingerprint || undefined,
          isEmailProfessional: checkEmailProfessional(
            referent.email,
            company,
            referent.name?.split(' ')[0], // firstName
            referent.name?.split(' ').slice(1).join(' '), // lastName
            response?.still_works_at_company
          ).isProfessional,
          hasSigned: !!response?.signed_document_url,
          // ✅ AJOUT : Données dates candidat
          candidate_collaboration_start_date: referent.candidate_collaboration_start_date,
          candidate_collaboration_end_date: referent.candidate_collaboration_end_date,
          candidate_still_working_together: referent.candidate_still_working_together,
          // ✅ AJOUT : Données dates référent exposées directement
          referent_collaboration_start_date: response?.collaboration_start_date,
          referent_collaboration_end_date: response?.collaboration_end_date,
          referent_still_works_at_company: response?.still_works_at_company,
          referent_still_working_together: response?.still_working_together, // ✅ NOUVEAU : Champ pour la logique des dates
          // ✅ PHASE 1 : Données relation
          relation_type: referent.relation_type, // Type attendu par le candidat/recruteur
          relation_type_confirmed: response?.relation_type_confirmed // Type confirmé par le référent
        };
      });

      logger.debug('Processed referents', { count: processedReferents.length });
      return processedReferents;
    } catch (err) {
      console.error('Error in getReferentsFromCampaign:', err);
      return [];
    }
  }, []);

  // Get critical responses from actual referent responses data
  const getCriticalResponses = useCallback(async (campaignId: string, aiSynthesis?: any) => {
    if (!campaignId) {
      console.warn('getCriticalResponses called without campaignId');
      return {
        wouldRecommend: [],
        wouldRehire: [],
        hasIncidents: null,
        confidenceLevel: 0
      };
    }

    try {
      logger.debug('Fetching critical responses for campaign', { campaignId });
      
      // Get responses with referent names
      const { data: responses, error } = await supabase
        .from('referent_responses')
        .select(`
          would_recommend, 
          would_rehire, 
          has_incidents, 
          reliability_score, 
          performance_score,
          referents!inner(name)
        `)
        .eq('campaign_id', campaignId);

      if (error) {
        console.error('Error fetching responses:', error);
        return {
          wouldRecommend: [],
          wouldRehire: [],
          hasIncidents: null,
          confidenceLevel: aiSynthesis?.confiance_score || 0
        };
      }

      if (!responses || responses.length === 0) {
        logger.debug('No responses found for campaign', { campaignId });
        return {
          wouldRecommend: [],
          wouldRehire: [],
          hasIncidents: null,
          confidenceLevel: aiSynthesis?.confiance_score || 0
        };
      }

      // Transform responses to new format with referent names
      const wouldRecommendResponses = (responses as any[]).map(r => ({
        referentName: (r as any).referents?.name || 'Référent anonyme',
        value: (r as any).would_recommend
      }));

      const wouldRehireResponses = (responses as any[]).map(r => ({
        referentName: (r as any).referents?.name || 'Référent anonyme',
        value: (r as any).would_rehire
      }));

      // For incidents, keep the simple boolean logic (any incident = true)
      const hasIncidentsCount = (responses as any[]).filter(r => (r as any).has_incidents === true).length;

      const criticalData = {
        wouldRecommend: wouldRecommendResponses,
        wouldRehire: wouldRehireResponses,
        hasIncidents: hasIncidentsCount > 0, // Any incident reported
        // Utiliser le vrai score de confiance depuis l'AI synthesis (pas de valeur par défaut arbitraire)
        confidenceLevel: aiSynthesis?.confiance_score || 0
      };

      logger.debug('Critical responses calculated from real data', {
        wouldRecommendCount: wouldRecommendResponses.length,
        wouldRehireCount: wouldRehireResponses.length,
        hasIncidents: criticalData.hasIncidents
      });
      return criticalData;
    } catch (err) {
      console.error('Error in getCriticalResponses:', err);
      return {
        wouldRecommend: [],
        wouldRehire: [],
        hasIncidents: null,
        confidenceLevel: aiSynthesis?.confiance_score || 0
      };
    }
  }, []);

  // Get email-company coherence analysis for a campaign
  const getEmailCoherenceAnalysis = useCallback(async (campaignId: string) => {
    if (!campaignId) {
      console.warn('getEmailCoherenceAnalysis called without campaignId');
      return { overallScore: 100, results: [] };
    }

    try {
      logger.debug('Fetching email coherence analysis for campaign', { campaignId });

      // 1. Récupérer les données de la campagne (expériences extraites du CV)
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('ai_extracted_data')
        .eq('id', campaignId)
        .single();

      if (campaignError) {
        console.error('Error fetching campaign data for email coherence:', campaignError);
        return { overallScore: 100, results: [] };
      }

      const experiences = (campaignData as any)?.ai_extracted_data?.experiences || [];

      // 2. Récupérer les référents et leurs réponses
      const { data: referentData, error: referentError } = await supabase
        .from('referents')
        .select(`
          id,
          name,
          email,
          experience_id,
          referent_responses (
            still_works_at_company,
            collaboration_end_date
          )
        `)
        .eq('campaign_id', campaignId)
        .not('referent_responses', 'is', null);

      if (referentError) {
        console.error('Error fetching referents for email coherence:', referentError);
        return { overallScore: 100, results: [] };
      }

      // 3. Préparer les données pour l'analyse
      const referentsForAnalysis = (referentData as any[])
        .filter((ref: any) => ref.referent_responses && ref.referent_responses.length > 0)
        .map((ref: any) => {
          const experience = experiences.find((exp: any) => exp.id === ref.experience_id);
          const response = ref.referent_responses[0];
          
          // Extraire prénom et nom du champ name
          const nameParts = (ref.name || '').trim().split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          return {
            email: ref.email,
            companyFromCV: experience?.company || 'Entreprise inconnue',
            stillWorksAtCompany: response.still_works_at_company,
            collaborationEndDate: response.collaboration_end_date,
            firstName,
            lastName
          };
        });

      // 4. Effectuer l'analyse de cohérence
      const analysis = analyzeAllReferentsEmailCoherence(referentsForAnalysis);
      
      logger.debug('Email coherence analysis completed', { 
        campaignId, 
        overallScore: analysis.overallScore,
        coherentCount: analysis.coherentCount,
        incoherentCount: analysis.incoherentCount
      });

      return analysis;

    } catch (err) {
      console.error('Error in getEmailCoherenceAnalysis:', err);
      return { overallScore: 100, results: [] };
    }
  }, []);

  // Get date reliability analysis for a campaign
  const getDateReliabilityAnalysis = useCallback(async (campaignId: string) => {
    if (!campaignId) {
      console.warn('getDateReliabilityAnalysis called without campaignId');
      return { averageScore: 10, analysis: [] };
    }

    try {
      logger.debug('Fetching date reliability analysis for campaign', { campaignId });

      // 1. Récupérer les données de la campagne (expériences extraites du CV)
      const { data: campaignData, error: campaignError } = await supabase
        .from('campaigns')
        .select('ai_extracted_data')
        .eq('id', campaignId)
        .single();

      if (campaignError) {
        console.error('Error fetching campaign data for date analysis:', campaignError);
        return { averageScore: 10, analysis: [] };
      }

      const experiences = (campaignData as any)?.ai_extracted_data?.experiences || [];
      
      if (experiences.length === 0) {
        return { averageScore: 10, analysis: [] };
      }

      // 2. Récupérer les référents et leurs réponses
      const { data: referentData, error: referentError } = await supabase
        .from('referents')
        .select(`
          id,
          name,
          experience_id,
          referent_responses (
            collaboration_start_date,
            collaboration_end_date
          )
        `)
        .eq('campaign_id', campaignId)
        .not('referent_responses', 'is', null);

      if (referentError) {
        console.error('Error fetching referents for date analysis:', referentError);
        return { averageScore: 10, analysis: [] };
      }

      // 3. Transformer les données pour l'analyse
      const referentResponses = (referentData as any[])
        .filter((ref: any) => ref.referent_responses && ref.referent_responses.length > 0)
        .map((ref: any) => ({
          referent_id: ref.id,
          referent_name: ref.name,
          experience_id: ref.experience_id || '',
          collaboration_start_date: ref.referent_responses[0].collaboration_start_date,
          collaboration_end_date: ref.referent_responses[0].collaboration_end_date
        }));

      // 4. Effectuer l'analyse de fiabilité
      const analysis = analyzeCampaignDateReliability(experiences, referentResponses);
      
      // 5. Calculer le score moyen
      const averageScore = analysis.length > 0 
        ? analysis.reduce((sum, item) => sum + item.reliability.score, 0) / analysis.length 
        : 10;

      logger.debug('Date reliability analysis completed', { 
        campaignId, 
        averageScore, 
        analysisCount: analysis.length 
      });

      return { averageScore, analysis };

    } catch (err) {
      console.error('Error in getDateReliabilityAnalysis:', err);
      return { averageScore: 10, analysis: [] };
    }
  }, []);

  return {
    calculateReliabilityScore,
    getReferentsFromCampaign,
    getCandidateFingerprint,
    getCriticalResponses,
    getDateReliabilityAnalysis,
    getEmailCoherenceAnalysis
  };
};